/** Delete confirmation. `DELETE` is a hard delete with no in-use guard (no instance table yet). */
import type { Ref } from 'vue'
import type { AssignmentTemplateListRow } from './interfaces'

import { toPaginationValue } from '@shared/lib/routes'

import { useAssignmentTemplatesStore } from '../assignment-templates/store'
import { useAssignmentTemplateDeleteApi } from '../../api/assignment-templates/delete.hook'
import { useAssignmentTemplatesApi } from '../../api/assignment-templates/list.hook'

interface ModalDeleteAssignmentTemplateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTemplate: ComputedRef<AssignmentTemplateListRow | null>
  refDetail: ComputedRef<string>
  closeModal: () => void
  confirmDelete: () => Promise<void>
}

export const useModalDeleteAssignmentTemplate = (): ModalDeleteAssignmentTemplateReturn => {
  const store = useAssignmentTemplatesStore()
  const route = useRoute()
  const router = useRouter()

  const refIsLoading = ref(false)

  const refIsOpen = computed<boolean>(() => store.isModalDelete)
  const refTemplate = computed<AssignmentTemplateListRow | null>(() => store.getTemplateToDelete)

  const refDetail = computed<string>(() =>
    `Delete "${refTemplate.value?.title ?? ''}"? This cannot be undone.`)

  const closeModal = (): void => {
    store.setTemplateToDelete(null)
  }

  /** Steps back a page when the deleted row was the page's last one — nothing else clamps the page. */
  const refetchAfterDelete = async (): Promise<void> => {
    const page = toPaginationValue(route.query?.page, store.getCurrentPage)

    if (store.getTemplates.length <= 1 && page > 1) {
      const previousPage = page - 1
      store.setCurrentPage(previousPage)
      await router.push({
        query: {
          ...route.query,
          page: String(previousPage),
        },
      })
    }

    await useAssignmentTemplatesApi()
  }

  const confirmDelete = async (): Promise<void> => {
    const template = refTemplate.value
    if (!template || refIsLoading.value) return

    // Not in `onSuccess`: the refetch awaits a `router.push` the API hook would not await.
    const isDeleted = await useAssignmentTemplateDeleteApi({
      templateId: template.id,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    closeModal()

    if (isDeleted) {
      await refetchAfterDelete()
    }
  }

  return {
    refIsOpen,
    refIsLoading,
    refTemplate,
    refDetail,
    closeModal,
    confirmDelete,
  }
}
